/**
 * ZoomableImage
 * Renders an already-resolved src (blob URL or data URL) with:
 *   - Mouse wheel zoom  (ctrl not required)
 *   - Pinch-to-zoom     (touch)
 *   - Click+drag to pan when zoomed in
 *   - Double-click to reset
 *   - Toolbar: zoom in / zoom out / reset
 */
import React, { useRef, useState, useEffect, useCallback } from 'react';
import styles from './zoomable-image.module.css';

const MIN_SCALE = 1;
const MAX_SCALE = 10;
const ZOOM_STEP = 0.25;

export default function ZoomableImage({ src, alt = '' }) {
  const containerRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  // Touch pinch state
  const lastDist = useRef(null);

  // Clamp offset so the image never disappears completely
  const clampOffset = useCallback((ox, oy, sc) => {
    if (!containerRef.current) return { x: ox, y: oy };
    const { width: cw, height: ch } = containerRef.current.getBoundingClientRect();
    const maxX = (cw * sc - cw) / 2 + cw * 0.2;
    const maxY = (ch * sc - ch) / 2 + ch * 0.2;
    return {
      x: Math.min(maxX, Math.max(-maxX, ox)),
      y: Math.min(maxY, Math.max(-maxY, oy)),
    };
  }, []);

  const reset = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  }, []);

  // Mouse wheel zoom (centered on cursor position)
  const onWheel = useCallback((e) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP;
    setScale(prev => {
      const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev + delta));
      if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
      return next;
    });
  }, []);

  // Attach wheel with { passive: false } so we can call preventDefault
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [onWheel]);

  // Drag to pan
  const onMouseDown = (e) => {
    if (scale <= MIN_SCALE) return;
    dragging.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  };
  const onMouseMove = useCallback((e) => {
    if (!dragging.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setOffset(prev => clampOffset(prev.x + dx, prev.y + dy, scale));
  }, [clampOffset, scale]);
  const onMouseUp = () => { dragging.current = false; };

  // Touch pinch
  const getTouchDist = (t) =>
    Math.hypot(t[1].clientX - t[0].clientX, t[1].clientY - t[0].clientY);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) lastDist.current = getTouchDist(e.touches);
    if (e.touches.length === 1 && scale > MIN_SCALE) {
      dragging.current = true;
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && lastDist.current !== null) {
      e.preventDefault();
      const dist = getTouchDist(e.touches);
      const ratio = dist / lastDist.current;
      lastDist.current = dist;
      setScale(prev => {
        const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prev * ratio));
        if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
        return next;
      });
    } else if (e.touches.length === 1 && dragging.current) {
      const dx = e.touches[0].clientX - lastMouse.current.x;
      const dy = e.touches[0].clientY - lastMouse.current.y;
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setOffset(prev => clampOffset(prev.x + dx, prev.y + dy, scale));
    }
  };
  const onTouchEnd = () => {
    dragging.current = false;
    lastDist.current = null;
  };

  const pct = Math.round(scale * 100);

  return (
    <div className={styles.wrapper}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <button
          className={styles.toolBtn}
          onClick={() => setScale(p => Math.min(MAX_SCALE, p + ZOOM_STEP))}
          title="Zoom +"
        >＋</button>
        <span className={styles.pct}>{pct}%</span>
        <button
          className={styles.toolBtn}
          onClick={() => { setScale(p => { const n = Math.max(MIN_SCALE, p - ZOOM_STEP); if (n === MIN_SCALE) setOffset({x:0,y:0}); return n; }); }}
          title="Zoom -"
        >－</button>
        <button
          className={styles.toolBtn}
          onClick={reset}
          title="Restablecer"
        >⊙</button>
      </div>

      {/* Image container */}
      <div
        ref={containerRef}
        className={styles.container}
        style={{ cursor: scale > MIN_SCALE ? (dragging.current ? 'grabbing' : 'grab') : 'default' }}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onDoubleClick={reset}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <img
          src={src}
          alt={alt}
          className={styles.img}
          style={{
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
          }}
          draggable={false}
        />
      </div>
    </div>
  );
}
